import { EventEmitter } from "~/store/event";
import { Remote, wrap } from "comlink";
import type { EventsWorker, EventsWorkerOptions } from "./eventsWorker";
import { EventData, EventQuery } from "~/types/event";
// Extend the EventEmitter with our worker events
export const eventWorkerEvents = new EventEmitter<{
  'worker:data': [objectID: string, workerID: string, events: EventData[]]; // [objectID, workerID, payload]
  'worker:error': [objectID: string, workerID: string, error: Error]; // [objectID, workerID, error]
}>();

/**
 * EventClient is a class that initializes an events worker and provides a method to update events
 * 
 * @note This class is used to fetch events from the worker
 */
export class EventClient {
  private worker?: Remote<EventsWorker>;
  private instance?: Worker;

  /**
   * Initialize the events worker
   * @param type - The type of object to fetch events for
   * @param params - The parameters for the events query
   * @param token - The authentication token
   * @param updateCallback - The callback function to update the events
   * @param options - The options for the events worker
   */
  async initialize(
    params: EventQuery,
    token: string,
    updateCallback: <T = EventData[]>(id: string, workerID: string, events: T) => void,
    options: EventsWorkerOptions = {}
  ) {
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'update') {
        // eventWorkerEvents.emit('worker:data', e.data.objectID, e.data.workerID, e.data.events);
        updateCallback(e.data.objectID, e.data.workerID, e.data.events);
      } else if (e.data?.type === 'error') {
        console.error('Events worker error:', e.data.error);
        // eventWorkerEvents.emit('worker:error', e.data.objectID, e.data.workerID, e.data.error);
      }
    };

    // Reuse a running worker - booting one re-parses the whole bundle (~100ms+
    // of CPU), and initialize() is re-dispatched on device/status/time changes
    // (same reuse pattern as MetricsClient, MCS-3969). Worker initialize()
    // stops polling and clears its buffer, so this is a full replace.
    if (this.worker && this.instance) {
      this.instance.onmessage = onMessage;
      await this.worker.initialize(params, token, options);
      return;
    }

    this.instance = new Worker(new URL('./eventsWorker', import.meta.url), {
      type: 'module',
      name: `events-worker-${options.name || ''}-${params.objectId || 'ALL'}`
    });
    this.worker = wrap<EventsWorker>(this.instance);
    this.instance.onmessage = onMessage;

    this.instance.onerror = (error) => {
      console.error('Worker error:', error);
    };

    await this.worker.initialize(params, token, options);
  }

  /**
   * Update the parameters for the events worker
   * @param params - The updated parameters for the events query
   * @param token - The authentication token
   */
  async updateParams(params: Partial<EventQuery>, token: string , options: EventsWorkerOptions = {}) {
    if (!this.worker) {
      console.warn('Worker not initialized before updateParams call');
      return null;
    }
    return this.worker.updateParams(params, token, options);
  }

  /**
   * Terminate the events worker
   */
  async terminate() {
    try {
      if (this.worker) {
        await this.worker.terminate();
        this.worker = undefined;
      }
      if (this.instance) {
        this.instance.terminate();
        this.instance = undefined;
      }
    } catch (error) {
      console.error('Error terminating events worker:', error);
    }
  }

  /**
   * Stop the events worker
   */
  stop() {
    try {
      this.worker?.stop();
    } catch (error) {
      console.error('Error stopping events worker:', error);
    }
  }

  getLatestQuery() {
    if (!this.worker) {
      console.warn('Worker not initialized before getLatestQuery call');
      return null;
    }
    return this.worker.getLatestQuery();
  }

  getWorkerID() {
    return this.worker?.getWorkerID();
  }
}